import {
  createContext,
  useCallback,
  useMemo,
  useState,
  useContext,
  ReactNode,
} from "react";
import { startOfDay, subDays } from "date-fns";
import { PeriodType as ApiPeriodType } from "../../../common/data/service-proxies";

export type LocalPeriodType = "weekly" | "monthly";

export interface IdleReportContextValue {
  dateFrom: Date;
  dateTo: Date;
  periodType: LocalPeriodType;
  presetPeriod: unknown;
  isDownloading: boolean;
  apiPeriodType: ApiPeriodType | undefined;
  reportParams: {
    fromDate: Date;
    toDate: Date;
    periodType: ApiPeriodType | undefined;
  };
  setDateFrom: (d: Date) => void;
  setDateTo: (d: Date) => void;
  handlePeriodChange: (value: unknown) => void;
  setPresetPeriod: (v: unknown) => void;
}

export const IdleReportContext = createContext<IdleReportContextValue | null>(
  null,
);

export const useIdleReport = (): IdleReportContextValue => {
  const ctx = useContext(IdleReportContext);
  if (ctx == null) {
    throw new Error("useIdleReport must be used within IdleReportProvider");
  }
  return ctx;
};

interface IdleReportProviderProps {
  children: ReactNode;
}

export const IdleReportProvider = ({ children }: IdleReportProviderProps) => {
  const [dateFrom, setDateFrom] = useState<Date>(() =>
    subDays(startOfDay(new Date()), 6),
  );
  const [dateTo, setDateTo] = useState<Date>(() => startOfDay(new Date()));
  const [periodType, setPeriodType] = useState<LocalPeriodType>("weekly");
  const [isDownloading] = useState(false);
  const [presetPeriod, setPresetPeriod] = useState<unknown>("");

  const apiPeriodType = useMemo<ApiPeriodType | undefined>(
    () =>
      periodType === "weekly" ? ApiPeriodType.Weekly : ApiPeriodType.Monthly,
    [periodType],
  );

  const reportParams = useMemo(
    () => ({
      fromDate: dateFrom,
      toDate: dateTo,
      periodType: apiPeriodType,
    }),
    [dateFrom, dateTo, apiPeriodType],
  );

  const handlePeriodChange = useCallback((value: unknown) => {
    const today = startOfDay(new Date());
    switch (value) {
      case "thisWeek":
        setPeriodType("weekly");
        setDateTo(today);
        setDateFrom(subDays(today, 6));
        break;
      case "lastWeek": {
        const end = subDays(today, 7);
        setPeriodType("weekly");
        setDateTo(end);
        setDateFrom(subDays(end, 6));
        break;
      }
      case "thisMonth":
        setPeriodType("monthly");
        setDateTo(today);
        setDateFrom(new Date(today.getFullYear(), today.getMonth(), 1));
        break;
      case "lastMonth": {
        const firstThisMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          1,
        );
        const lastMonthEnd = subDays(firstThisMonth, 1);
        setPeriodType("monthly");
        setDateTo(lastMonthEnd);
        setDateFrom(
          new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1),
        );
        break;
      }
      default:
        break;
    }
  }, []);

  const value = useMemo<IdleReportContextValue>(
    () => ({
      dateFrom,
      dateTo,
      periodType,
      presetPeriod,
      isDownloading,
      apiPeriodType,
      reportParams,
      setDateFrom,
      setDateTo,
      handlePeriodChange,
      setPresetPeriod,
    }),
    [
      dateFrom,
      dateTo,
      periodType,
      presetPeriod,
      isDownloading,
      apiPeriodType,
      reportParams,
      handlePeriodChange,
    ],
  );

  return (
    <IdleReportContext.Provider value={value}>
      {children}
    </IdleReportContext.Provider>
  );
};
